import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lock, ShieldCheck, Languages, Timer, Activity, HeartHandshake, Wind, Zap, Brain, Leaf, EyeOff, UserX, BellOff, Trash2, ClipboardList, UserCheck, ArrowRight } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import { api, track } from "../lib/api";
import { useSeo } from "../lib/seo";
import { QuestionCard } from "../components/QuestionCard";

const HERO_IMG = "/img/hero-run.jpg?v=2";

const GOALS = [
  ["stamina-control", Timer, "Stamina & Control", "Learn about arousal, control, pelvic health, breathing, confidence, and healthy sexual habits."],
  ["erection-confidence", HeartHandshake, "Erection Confidence", "Understand erection health and habits related to cardiovascular health, stress, sleep, fitness, and confidence."],
  ["pelvic-health", Activity, "Pelvic Health", "Understand and train your pelvic-floor muscles correctly."],
  ["energy-libido", Zap, "Energy & Libido", "Explore sleep, physical activity, stress, nutrition, relationships, and sexual desire."],
  ["performance-anxiety", Brain, "Performance Anxiety", "Understand the anxiety-performance cycle and learn tools for relaxation and confidence."],
  ["mens-wellness", Leaf, "Overall Men's Wellness", "Build stronger habits around sleep, movement, nutrition, stress, and overall health."],
];

const PRIVACY = [
  [UserX, "Community participation is anonymous", "You appear only as \"Anonymous #4821\". Your email is never shown."],
  [EyeOff, "No real name needed publicly", "Pick any alias. Only you see it."],
  [BellOff, "Discreet notifications", "\"Your daily wellness routine is ready.\" Nothing embarrassing, ever."],
  [ShieldCheck, "You control your profile", "Choose what to share, like your age range — or nothing at all."],
  [Trash2, "Delete anytime", "Export or permanently delete your account and data from settings."],
  [Lock, "Sensitive by default", "Wellness discussions are treated as sensitive information and moderated before publishing."],
];

function Hero() {
  const { t } = useI18n();
  return (
    <section className="relative overflow-hidden">
      <div className="mx-auto grid max-w-6xl items-center gap-10 px-4 pb-12 pt-8 sm:px-6 lg:grid-cols-[1.1fr_.9fr] lg:pt-16">
        <div className="ckb-rise space-y-6">
          <span className="ckb-eyebrow">Private men's wellness, built for India</span>
          <h1 className="font-display text-4xl font-bold leading-[1.05] tracking-tight text-ckb-ink sm:text-5xl lg:text-6xl" data-testid="hero-headline">{t("hero_title")}</h1>
          <p className="max-w-xl text-base text-ckb-ink2 sm:text-lg">{t("hero_sub")}</p>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Link to="/assessment" className="ckb-btn-primary" data-testid="start-assessment-cta">{t("hero_cta")} <ArrowRight className="h-4 w-4" /></Link>
            <Link to="/programs" className="ckb-btn-ghost" data-testid="explore-programs-cta">{t("hero_cta2")}</Link>
          </div>
          <div className="flex flex-wrap gap-2 pt-2 text-sm text-ckb-ink2" data-testid="trust-indicators">
            <span className="flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5"><Lock className="h-3.5 w-3.5 text-ckb-forest" /> {t("trust_private")}</span>
            <span className="flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5"><ShieldCheck className="h-3.5 w-3.5 text-ckb-forest" /> {t("trust_conf")}</span>
            <span className="flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5"><Languages className="h-3.5 w-3.5 text-ckb-forest" /> English • हिंदी • Hinglish</span>
          </div>
          <p className="font-display text-sm font-semibold text-ckb-forest">{t("no_judgement")} <span className="font-normal text-ckb-muted">· 100% free</span></p>
        </div>
        <div className="relative ckb-rise" style={{ animationDelay: ".15s" }}>
          <div className="grain relative aspect-[4/5] overflow-hidden rounded-[2.5rem] bg-ckb-sand2">
            <img src={HERO_IMG} alt="A man running outdoors towards the rising sun" className="h-full w-full object-cover" data-testid="home-hero-image" loading="eager" />
          </div>
          <div className="absolute -bottom-5 left-4 right-4 rounded-3xl border border-white/60 bg-white/80 p-4 shadow-lg backdrop-blur-xl sm:left-auto sm:w-64">
            <div className="text-xs text-ckb-muted">Today's wellness plan</div>
            <div className="mt-1 space-y-1 text-sm font-medium text-ckb-ink">
              <div className="flex justify-between"><span>Pelvic-floor awareness</span><span className="text-ckb-muted">5 min</span></div>
              <div className="flex justify-between"><span>Breathing</span><span className="text-ckb-muted">5 min</span></div>
              <div className="flex justify-between"><span>Daily check-in</span><span className="text-ckb-muted">1 min</span></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const Section = ({ eyebrow, title, children, className = "", testId }) => (
  <section className={`mx-auto max-w-6xl px-4 py-14 sm:px-6 ${className}`} data-testid={testId}>
    {eyebrow && <span className="ckb-eyebrow">{eyebrow}</span>}
    <h2 className="mt-2 max-w-2xl font-display text-2xl font-bold tracking-tight text-ckb-ink sm:text-3xl">{title}</h2>
    <div className="mt-8">{children}</div>
  </section>
);

export default function Home() {
  const { t } = useI18n();
  const [posts, setPosts] = useState([]);
  useSeo({ title: "Private men's wellness, built for India", path: "/" });
  useEffect(() => {
    track("homepage_visit");
    api.get("/community/questions?tab=popular").then((r) => setPosts(r.data.items.slice(0, 3))).catch(() => {});
  }, []);
  return (
    <main className="pb-10" data-testid="home-page">
      <Hero />
      <Section eyebrow="Six free programs" title={t("goals_title")} testId="goals-section">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {GOALS.map(([slug, Icon, title, text], i) => (
            <Link key={slug} to={`/programs/${slug}`} className={`group ckb-card p-6 transition-[transform,box-shadow] duration-200 hover:-translate-y-1 hover:shadow-md ${i === 0 ? "lg:row-span-1 bg-ckb-forest text-white" : ""}`} data-testid={`goal-card-${slug}`}>
              <Icon className={`h-6 w-6 ${i === 0 ? "text-ckb-sand" : "text-ckb-forest"}`} aria-hidden />
              <h3 className={`mt-4 font-display text-lg font-semibold ${i === 0 ? "text-white" : "text-ckb-ink"}`}>{title}</h3>
              <p className={`mt-2 text-sm leading-relaxed ${i === 0 ? "text-white/80" : "text-ckb-ink2"}`}>{text}</p>
              <span className={`mt-4 inline-flex items-center gap-1 text-sm font-medium ${i === 0 ? "text-ckb-sand" : "text-ckb-forest"}`}>21 days · Free <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
            </Link>
          ))}
        </div>
      </Section>
      <Section eyebrow="Simple and private" title={t("how_title")} className="!pt-4" testId="how-it-works-section">
        <ol className="grid gap-4 md:grid-cols-4">
          {[ClipboardList, UserCheck, Wind, Activity].map((Icon, i) => (
            <li key={i} className="relative rounded-3xl bg-ckb-sand2/70 p-6">
              <span className="font-display text-4xl font-bold text-ckb-sage/50">0{i + 1}</span>
              <Icon className="mt-3 h-5 w-5 text-ckb-forest" aria-hidden />
              <p className="mt-2 font-medium text-ckb-ink">{t(`how_${i + 1}`)}</p>
            </li>
          ))}
        </ol>
      </Section>
      <section className="bg-ckb-forest py-16 text-white" data-testid="privacy-section">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <span className="text-xs font-semibold uppercase tracking-[0.18em] text-ckb-sand/70">Privacy first</span>
          <h2 className="mt-2 font-display text-2xl font-bold sm:text-3xl">{t("privacy_title")}</h2>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {PRIVACY.map(([Icon, title, text]) => (
              <div key={title} className="rounded-3xl border border-white/10 bg-white/5 p-6">
                <Icon className="h-5 w-5 text-ckb-sand" aria-hidden />
                <h3 className="mt-3 font-semibold">{title}</h3>
                <p className="mt-1 text-sm text-white/75">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <Section eyebrow="Anonymous community" title={t("community_preview")} testId="community-preview-section">
        <div className="grid gap-4 md:grid-cols-3">
          {posts.map((q) => <QuestionCard key={q.id} q={q} />)}
        </div>
        <p className="mt-4 text-xs text-ckb-muted">Every question and reply is checked for privacy and safety before it goes live. Community experiences are not medical advice.</p>
        <Link to="/community" className="ckb-btn-ghost mt-6" data-testid="explore-community-cta">{t("explore_community")} <ArrowRight className="h-4 w-4" /></Link>
      </Section>
      <section className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="rounded-[2rem] bg-ckb-mint p-8 sm:p-12">
          <h2 className="font-display text-2xl font-bold text-ckb-ink sm:text-3xl">Start with three quiet minutes.</h2>
          <p className="mt-2 max-w-lg text-ckb-ink2">No sign-up needed to take the assessment. Everything in {""}V1 is free.</p>
          <Link to="/assessment" className="ckb-btn-primary mt-6" data-testid="bottom-assessment-cta">{t("hero_cta")}</Link>
        </div>
      </section>
    </main>
  );
}
